import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Container,
  Header,
  FormField,
  InputProps,
  Modal,
  SelectProps,
  SpaceBetween,
  Tabs,
  Tiles,
} from "@amzn/awsui-components-react";
import {
  OmsPresetRequest,
  OmsPresetScope,
  OmsProduct,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import useOmsExport, { restoredOmsExportMode } from "../../hooks/useOmsExport";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import useOmsModalHeight from "../../hooks/useOmsModalHeight";
import useOmsPresets, {
  presetErrorMessage,
  presetValidationSourceUnavailable,
  useOmsExportCapabilities,
} from "../../hooks/useOmsPresets";
import { PresetRetryExpiredError } from "../../hooks/omsPresetRetry";
import {
  hasPendingOmsRequest,
  pendingOmsMutation,
} from "../../hooks/omsDraftStorage";
import OMSColumns, {
  columnSelectionError,
  OmsColumnsDraft,
} from "./OMSColumns";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSExportSource from "./OMSExportSource";
import OMSPresetToolbar from "./OMSPresetToolbar";
import OMSSettingsSummary from "./OMSSettingsSummary";
import OMSExportReview from "./OMSExportReview";
import { filterIssue, OmsFilterIssue } from "./omsConfigurationValidation";
import { submittedCondition } from "./omsFilterEditing";
import OMSPresetChanges, { OMSPresetContents } from "./OMSPresetChanges";
import { OMSPresetManager, OMSPresetSave } from "./OMSPresetEditor";
import OMSRecordFilters from "./OMSRecordFilters";
import {
  applyPreset,
  OmsConfiguration,
  presetIsEdited,
} from "./omsPresetEditing";

export default function OMSExportDialog({
  product,
  productLabel,
  report,
  availableReport,
  onClose,
  draft,
  onSaveDraft,
  resumeExport = true,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  availableReport: OmsReport;
  onClose: (draft: OmsFilterDraft) => void;
  draft?: OmsFilterDraft;
  onSaveDraft: (draft: OmsFilterDraft) => void;
  resumeExport?: boolean;
}) {
  const [mode, setMode] = useState<"full" | "filtered">(() => {
    if (draft?.viewingPrevious) return draft.viewingPrevious.mode;
    if (draft) return draft.tab === "filtered" ? "filtered" : "full";
    return resumeExport ? restoredOmsExportMode(product, report) : "full";
  });
  const [focusFilters, setFocusFilters] = useState(false);
  const [focusColumns, setFocusColumns] = useState(false);
  const [workspace, setWorkspace] = useState<"rows" | "columns">(
    draft?.workspace || "rows"
  );
  const [columns, setColumns] = useState<OmsColumnsDraft>(
    draft?.columns
      ? {
          ...draft.columns,
          customOrder:
            draft.columns.customOrder ?? Boolean(draft.columns.names.length),
        }
      : { strategy: "all", names: [], customOrder: false }
  );
  const [applied, setApplied] = useState(draft?.appliedPreset);
  const [undo, setUndo] = useState<OmsConfiguration | undefined>(draft?.undo);
  const [announcement, setAnnouncement] = useState("");
  const [restoredNotice, setRestoredNotice] = useState(
    hasPendingOmsRequest(draft)
  );
  const [attempted, setAttempted] = useState(false);
  const [focusIssue, setFocusIssue] = useState<OmsFilterIssue>();
  const [reviewingExport, setReviewingExport] = useState(false);
  const exportReviewRef = useRef<ButtonProps.Ref>(null);
  const reviewReturnFocus = useRef<"review" | "filters" | "columns">();
  useEffect(() => {
    if (reviewingExport || !reviewReturnFocus.current) return undefined;
    const timer = window.setTimeout(() => {
      // Let the replacement modal capture focus before focusing a settings control.
      if (reviewReturnFocus.current === "filters") setFocusFilters(true);
      else if (reviewReturnFocus.current === "columns") setFocusColumns(true);
      else exportReviewRef.current?.focus();
      reviewReturnFocus.current = undefined;
    }, 0);
    return () => window.clearTimeout(timer);
  }, [reviewingExport]);
  const [undoRequests, setUndoRequests] = useState(draft?.undoRequests);
  const [previousDraft, setPreviousDraft] = useState<
    OmsFilterDraft | undefined
  >(
    draft?.viewingPrevious
      ? { ...draft, viewingPrevious: undefined }
      : undefined
  );
  const [editor, setEditor] = useState<
    NonNullable<OmsFilterDraft["presetEditor"]>
  >(draft?.presetEditor || { view: "export", name: "", scope: "columns" });
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [saveValidation, setSaveValidation] = useState("");
  const saveRef = useRef<ButtonProps.Ref>(null);
  const saveNames = useRef<{ existing: string; new: string }>({
    existing: applied?.name || "",
    new: "",
  });
  const manageRef = useRef<ButtonProps.Ref>(null);
  const selectRef = useRef<SelectProps.Ref>(null);
  const nameRef = useRef<InputProps.Ref>(null);
  const editGeneration = useRef(0);
  const capabilities = useOmsExportCapabilities();
  const presetsEnabled = Boolean(capabilities.data?.presets);
  const columnsEnabled = Boolean(capabilities.data?.columnSelection);
  const presets = useOmsPresets(product, presetsEnabled, draft?.presetRequest);
  const full = useOmsExport(
    product,
    draft?.report || report,
    "full",
    availableReport,
    (Boolean(draft?.viewingPrevious) || (resumeExport && !draft?.editing)) &&
      mode === "full",
    draft?.requests?.full ||
      (draft?.request?.mode === "full" ? draft.request : undefined)
  );
  const filtered = useOmsExport(
    product,
    draft?.report || report,
    "filtered",
    availableReport,
    (Boolean(draft?.viewingPrevious) || (resumeExport && !draft?.editing)) &&
      mode === "filtered",
    draft?.requests?.filtered ||
      (draft?.request?.mode === "filtered" ? draft.request : undefined)
  );
  const current = mode === "full" ? full : filtered;
  const source = current.sourceReport;
  const criteria = useOmsFilterCriteria(
    source,
    mode === "filtered" && !previousDraft ? current.job.data : undefined,
    draft
  );
  const active = Boolean(current.jobId) || current.create.isLoading;
  const accepted = current.job.data;
  const restored = useRef<string>();
  useEffect(() => {
    if (previousDraft || !accepted || restored.current === accepted.jobId)
      return;
    restored.current = accepted.jobId;
    setColumns((previous) => ({
      strategy: accepted.outputColumnStrategy || "all",
      customOrder: true,
      names:
        accepted.outputColumnStrategy === "selected"
          ? accepted.outputColumns || []
          : previous.names,
    }));
  }, [accepted, previousDraft]);
  const configuration: OmsConfiguration = {
    mode,
    criteria: criteria.criteria,
    operation: criteria.operation,
    columns,
    appliedPreset: applied,
  };
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;
  const definitionEdited =
    (editor.view === "edit" || (editor.view === "save" && editor.updating)) &&
    (editor.name.trim().normalize("NFC") !== applied?.name ||
      editor.scope !== applied?.scope);
  const edited = Boolean(
    applied && (presetIsEdited(applied, configuration) || definitionEdited)
  );
  const columnError = columnSelectionError(columns, source);
  const validFilters = mode === "full" || criteria.valid;
  const previousRequest = current.matchesPreviousRequest({
    filters: mode === "full" ? [] : criteria.filters,
    operation: mode === "full" ? "and" : criteria.operation,
    outputColumns: columns.strategy === "selected" ? columns.names : undefined,
  });
  const validColumns =
    !columnError &&
    (columnsEnabled || columns.strategy === "all" || previousRequest);
  const preparing =
    current.create.isLoading ||
    (Boolean(current.jobId) &&
      !current.expired &&
      !current.unavailable &&
      !current.job.isError &&
      (!accepted || accepted.status === "PREPARING"));
  const {
    height,
    width,
    contentRef,
    titleRef,
    footerRef,
    paneRef,
    paneMaxHeight,
  } = useOmsModalHeight(
    workspace === "columns" ? 600 : 480,
    editor.view === "manage" || editor.view === "save" ? 1000 : 1520
  );
  const editingPreset = editor.view === "edit";
  useEffect(() => {
    if (current.jobId && current.create.isSuccess && editor.view === "edit")
      setEditor({ view: "export", name: "", scope: "columns" });
  }, [current.jobId, current.create.isSuccess, editor.view]);
  const issues =
    mode === "filtered"
      ? criteria.criteria
          .map((criterion, index) => filterIssue(source, criterion, index))
          .filter((issue): issue is OmsFilterIssue => Boolean(issue))
      : [];
  const completeFilters =
    mode === "full"
      ? []
      : criteria.criteria
          .filter((criterion, index) => !filterIssue(source, criterion, index))
          .map(submittedCondition);

  function validateConfiguration() {
    setAttempted(true);
    if (issues.length) {
      setWorkspace("rows");
      setFocusIssue(issues[0]);
      setFocusFilters(true);
      return false;
    }
    if (!validColumns) {
      setWorkspace("columns");
      setFocusColumns(true);
      return false;
    }
    return true;
  }
  function returnToDraft() {
    if (!previousDraft) return;
    const checkpoint = previousDraft;
    full.editSettings(checkpoint.report || report);
    filtered.editSettings(checkpoint.report || report);
    setMode(checkpoint.tab === "filtered" ? "filtered" : "full");
    criteria.replace(checkpoint.criteria, checkpoint.operation);
    setColumns(checkpoint.columns || { strategy: "all", names: [] });
    setApplied(checkpoint.appliedPreset);
    setWorkspace(checkpoint.workspace || "rows");
    full.restoreRetryIdentity(checkpoint.requests?.full);
    filtered.restoreRetryIdentity(checkpoint.requests?.filtered);
    setEditor(
      checkpoint.presetEditor || { view: "export", name: "", scope: "columns" }
    );
    setUndo(checkpoint.undo);
    setUndoRequests(checkpoint.undoRequests);
    setPreviousDraft(undefined);
    setAnnouncement("Editing draft restored.");
  }

  function changed() {
    editGeneration.current += 1;
    setUndo(undefined);
    setUndoRequests(undefined);
    setAnnouncement("");
  }
  function restore(next: OmsConfiguration, nextSource = source) {
    // A reversible preset edit must retain an uncertain request's retry identity.
    if (full.jobId || full.sourceReport.snapshotId !== nextSource.snapshotId)
      full.editSettings(nextSource);
    if (
      filtered.jobId ||
      filtered.sourceReport.snapshotId !== nextSource.snapshotId
    )
      filtered.editSettings(nextSource);
    setMode(next.mode);
    criteria.replace(next.criteria, next.operation);
    setColumns(next.columns);
    setApplied(next.appliedPreset);
  }
  function undoSettings() {
    if (!undo) return;
    restore(undo);
    full.restoreRetryIdentity(undoRequests?.full);
    filtered.restoreRetryIdentity(undoRequests?.filtered);
    setUndo(undefined);
    setUndoRequests(undefined);
    setAttempted(false);
    setAnnouncement("Previous rows and columns restored.");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  }
  const selectPreset = async (id: string, editing = false) => {
    const generation = editGeneration.current;
    try {
      const preset = await presets.read.mutateAsync(id);
      if (generation !== editGeneration.current) {
        setAnnouncement(
          "Settings changed while the preset loaded. Choose it again to apply."
        );
        return undefined;
      }
      const previous = configurationRef.current;
      const previousRequests = {
        full: full.requestIdentity() || undefined,
        filtered: filtered.requestIdentity() || undefined,
      };
      const next = applyPreset(preset, previous, source);
      restore(next);
      setUndo(previous);
      setUndoRequests(previousRequests);
      setAnnouncement(
        editing
          ? `Editing ${preset.name}. Review changes before saving.`
          : `Applied ${preset.name}.`
      );
      if (editing)
        setEditor({
          view: "edit",
          name: preset.name,
          scope: preset.scope,
          updating: true,
          previous,
          previousReport: source,
          previousWorkspace: workspace,
          previousRequests,
          cancelTo: editor.view === "manage" ? "manage" : "export",
        });
      if (columnSelectionError(next.columns, source)) {
        setWorkspace("columns");
        setFocusColumns(true);
      } else if (preset.scope !== "columns") {
        setWorkspace("rows");
        setFocusFilters(true);
      } else {
        setWorkspace("columns");
        setFocusColumns(true);
      }
      return preset;
    } catch {
      return undefined;
    }
  };
  const requestPreset = (id: string, editing = false) => {
    selectPreset(id, editing).catch(() => undefined);
  };
  const openSave = (
    updating = false,
    returnTo: "save" | "actions" | "edit" = "save"
  ) => {
    presets.change.reset();
    presets.read.reset();
    setSaveValidation("");
    setSaveAttempted(false);
    let scope: OmsPresetScope = "columns";
    let name = "";
    if (updating && applied)
      scope = editor.view === "edit" ? editor.scope : applied.scope;
    else if (validFilters && validColumns) scope = "both";
    if (updating && applied)
      name = editor.view === "edit" ? editor.name : applied.name;
    saveNames.current = {
      existing: editor.view === "edit" ? editor.name : applied?.name || "",
      new: "",
    };
    setEditor({
      ...editor,
      view: "save",
      name,
      scope,
      updating,
      returnTo,
      saveCheckpoint: {
        configuration,
        report: source,
        workspace,
        requests: {
          full: full.requestIdentity() || undefined,
          filtered: filtered.requestIdentity() || undefined,
        },
      },
    });
  };
  const backToExport = (restoreCheckpoint = true) => {
    if (restoreCheckpoint && editor.view === "save" && editor.saveCheckpoint) {
      const checkpoint = editor.saveCheckpoint;
      restore(checkpoint.configuration, checkpoint.report);
      setWorkspace(checkpoint.workspace);
      full.restoreRetryIdentity(checkpoint.requests?.full);
      filtered.restoreRetryIdentity(checkpoint.requests?.filtered);
    }
    setEditor({
      ...editor,
      name:
        editor.view === "save" && editor.returnTo === "edit" && !editor.updating
          ? saveNames.current.existing
          : editor.name,
      view: editor.returnTo === "edit" ? "edit" : "export",
    });
    window.setTimeout(() => {
      if (editor.view === "manage") manageRef.current?.focus();
      else if (editor.returnTo === "actions") saveRef.current?.focus();
      else if (saveRef.current) saveRef.current.focus();
      else manageRef.current?.focus();
    }, 0);
  };
  const changeSaveDestination = (updating: boolean) => {
    saveNames.current[editor.updating ? "existing" : "new"] = editor.name;
    setEditor({
      ...editor,
      updating,
      name: saveNames.current[updating ? "existing" : "new"],
    });
    setSaveValidation("");
    setSaveAttempted(false);
  };
  const cancelEditing = () => {
    if (editor.previous) {
      restore(editor.previous, editor.previousReport || source);
      if (editor.previousRequests) {
        full.restoreRetryIdentity(editor.previousRequests.full);
        filtered.restoreRetryIdentity(editor.previousRequests.filtered);
      }
    }
    if (editor.previousWorkspace) setWorkspace(editor.previousWorkspace);
    setEditor({
      view: editor.cancelTo || "export",
      name: "",
      scope: "columns",
    });
    setUndo(undefined);
    setAnnouncement("");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  };
  const savePreset = () => {
    setSaveAttempted(true);
    const name = editor.name.trim().normalize("NFC");
    if (!name || Array.from(name).length > 80 || /\p{C}/u.test(name)) {
      setSaveValidation(
        "Enter a name of 1–80 characters without control characters."
      );
      nameRef.current?.focus();
      return;
    }
    if (
      (editor.scope !== "columns" && !validFilters) ||
      (editor.scope !== "filters" && !validColumns)
    ) {
      setSaveValidation("Correct the settings included in this preset.");
      return;
    }
    const body: OmsPresetRequest = {
      name,
      scope: editor.scope,
      validationSnapshotId: source.snapshotId,
      ...(editor.scope !== "columns"
        ? {
            rows: mode === "full" ? "all" : "filtered",
            filters: mode === "full" ? [] : criteria.filters,
            operation: mode === "full" ? "and" : criteria.operation,
          }
        : {}),
      ...(editor.scope !== "filters"
        ? {
            columns:
              columns.strategy === "all"
                ? { strategy: "all" }
                : { strategy: "selected", names: columns.names },
          }
        : {}),
    };
    if (new Blob([JSON.stringify(body)]).size > 16 * 1024) {
      setSaveValidation(
        "These settings exceed the 16 KiB preset limit. You can still export them without saving."
      );
      return;
    }
    setSaveValidation("");
    presets.change
      .mutateAsync({
        action: "save",
        body,
        existing: editor.updating ? applied : undefined,
      })
      .then((result) => {
        if (result.preset) setApplied(result.preset);
        setAnnouncement(`Saved ${name}.`);
        setUndo(undefined);
        setEditor({ view: "export", name: "", scope: "columns" });
        window.setTimeout(() => {
          if (active) saveRef.current?.focus();
          else selectRef.current?.focus();
        }, 0);
      })
      .catch(() => nameRef.current?.focus());
  };
  const retryRequest = current.requestIdentity();
  const editingDraft: OmsFilterDraft = {
    snapshotId: source.snapshotId,
    report: source,
    editing: !active,
    pendingExport:
      pendingOmsMutation(full.create) ||
      pendingOmsMutation(filtered.create) ||
      Boolean(
        draft?.pendingExport && full.create.isIdle && filtered.create.isIdle
      ),
    pendingPreset:
      pendingOmsMutation(presets.change) ||
      Boolean(
        draft?.pendingPreset &&
          presets.change.isIdle &&
          presets.changeIdentity()
      ),
    criteria: criteria.criteria,
    operation: criteria.operation,
    tab: mode,
    columns,
    workspace,
    appliedPreset: applied,
    presetEditor: editor,
    request: retryRequest ? { mode, ...retryRequest } : undefined,
    presetRequest: presets.changeIdentity(),
    undo,
    undoRequests,
    requests: {
      full: full.requestIdentity() || undefined,
      filtered: filtered.requestIdentity() || undefined,
    },
  };
  const recoveryDraft: OmsFilterDraft = previousDraft
    ? { ...previousDraft, viewingPrevious: { mode } }
    : editingDraft;
  const serializedDraft = JSON.stringify(recoveryDraft);
  useEffect(() => {
    onSaveDraft(JSON.parse(serializedDraft) as OmsFilterDraft);
  }, [serializedDraft, onSaveDraft]);
  const close = () => {
    onClose(recoveryDraft);
  };
  const editableCriteria = {
    ...criteria,
    update: (...args: Parameters<typeof criteria.update>) => {
      changed();
      criteria.update(...args);
    },
    setOperator: (...args: Parameters<typeof criteria.setOperator>) => {
      changed();
      criteria.setOperator(...args);
    },
    setColumn: (...args: Parameters<typeof criteria.setColumn>) => {
      changed();
      criteria.setColumn(...args);
    },
    setOperation: (...args: Parameters<typeof criteria.setOperation>) => {
      changed();
      criteria.setOperation(...args);
    },
    add: () => {
      changed();
      criteria.add();
    },
    remove: (index: number) => {
      changed();
      criteria.remove(index);
    },
    clear: () => {
      changed();
      criteria.clear();
    },
  };
  const saveStatus = (
    presets.change.error as { response?: { status?: number } }
  )?.response?.status;
  let summaryColumns =
    columns.strategy === "all" ? source.columns : columns.names;
  let summaryStrategy = columns.strategy;
  if (active && accepted) {
    summaryColumns = accepted.outputColumns || source.columns;
    summaryStrategy = accepted.outputColumnStrategy || "all";
  }
  const columnSummary =
    summaryStrategy === "all"
      ? `All columns (${summaryColumns.length})`
      : `${summaryColumns.length} selected columns`;
  const title =
    editor.view === "manage"
      ? `Manage ${productLabel} presets`
      : `Export ${productLabel} report`;

  const expiredRequest =
    presets.change.error instanceof PresetRetryExpiredError;
  let saveErrorHeader = "Could not save preset";
  if (expiredRequest) saveErrorHeader = "Review the earlier request";
  if (presets.read.isError) saveErrorHeader = "Could not reload preset";
  let saveRecoveryAction: React.ReactNode;
  if (presets.change.error instanceof PresetRetryExpiredError) {
    if (presets.change.error.reconciled)
      saveRecoveryAction = (
        <Button
          onClick={() => {
            presets.restartExpiredRequest();
            setSaveValidation("");
            setSaveAttempted(false);
          }}
        >
          Start new save
        </Button>
      );
  } else if (
    saveStatus === 412 ||
    (saveStatus === 404 &&
      !presetValidationSourceUnavailable(presets.change.error))
  ) {
    saveRecoveryAction = (
      <SpaceBetween direction="horizontal" size="xs">
        {applied && (
          <Button
            loading={presets.read.isLoading}
            loadingText="Reloading preset"
            onClick={() => {
              selectPreset(applied.presetId)
                .then((reloaded) => {
                  if (!reloaded) return;
                  presets.change.reset();
                  setSaveValidation("");
                  backToExport(false);
                  setEditor((previous) => ({
                    ...previous,
                    name: reloaded.name,
                    scope: reloaded.scope,
                  }));
                })
                .catch(() => undefined);
            }}
          >
            Reload preset
          </Button>
        )}
        <Button
          disabled={presets.read.isLoading}
          onClick={() => {
            presets.change.reset();
            presets.read.reset();
            saveNames.current.existing = editor.name;
            setEditor({
              ...editor,
              updating: false,
              name: `${editor.name.slice(0, 75)} copy`,
            });
          }}
        >
          Save as new
        </Button>
      </SpaceBetween>
    );
  }

  const prepareAllowed =
    !presets.read.isLoading &&
    !presets.change.isLoading &&
    !(columns.strategy === "selected" && !columnsEnabled && !previousRequest);
  const exportActions = (
    <OMSExportActions
      state={current}
      filters={mode === "full" ? [] : criteria.filters}
      operation={mode === "full" ? "and" : criteria.operation}
      outputColumns={
        columns.strategy === "selected" ? columns.names : undefined
      }
      valid={prepareAllowed}
      onReview={reviewingExport ? undefined : () => setReviewingExport(true)}
      reviewRef={reviewingExport ? undefined : exportReviewRef}
      onPrepare={() => {
        const valid = validateConfiguration();
        if (reviewingExport) {
          if (valid) reviewReturnFocus.current = "review";
          else
            reviewReturnFocus.current = issues.length ? "filters" : "columns";
        }
        setReviewingExport(false);
        return valid;
      }}
      hideEditSettings={Boolean(previousDraft)}
      onConfigure={() => {
        setReviewingExport(false);
        changed();
        if (mode === "filtered") {
          setWorkspace("rows");
          setFocusFilters(true);
        }
      }}
    />
  );
  if (reviewingExport)
    return (
      <OMSExportReview
        productLabel={productLabel}
        report={source}
        mode={mode}
        filters={completeFilters}
        operation={criteria.operation}
        columns={columns.strategy === "all" ? source.columns : columns.names}
        allColumns={columns.strategy === "all"}
        incomplete={issues.length}
        onBack={() => {
          reviewReturnFocus.current = "review";
          setReviewingExport(false);
        }}
        actions={exportActions}
      />
    );

  return (
    <Modal
      visible
      onDismiss={close}
      header={<span ref={titleRef}>{title}</span>}
      size="xx-large"
      width={width}
      position="top"
      height={height}
      closeAriaLabel="Close report export"
      footer={
        <div ref={footerRef} className="oms-report-download__footer">
          {(editor.view === "export" || editingPreset) && (
            <>
              {(!active ||
                previousDraft ||
                editingPreset ||
                (presetsEnabled && accepted?.status === "READY")) && (
                <div className="oms-report-download__footer-context">
                  {!active && (
                    <div
                      className="oms-export-summary"
                      role="group"
                      aria-label="Export settings"
                    >
                      <Box fontWeight="bold">
                        {mode === "full" ? "All rows" : "Filtered rows"}
                        {" · "}
                        {columnSummary}
                      </Box>
                      <Box color="text-body-secondary" fontSize="body-s">
                        CSV in a ZIP
                      </Box>
                    </div>
                  )}
                  {previousDraft && (
                    <Button onClick={() => returnToDraft()}>
                      Return to draft
                    </Button>
                  )}
                  {editingPreset && (
                    <Button onClick={() => cancelEditing()}>
                      Cancel editing
                    </Button>
                  )}
                  {presetsEnabled &&
                    active &&
                    !previousDraft &&
                    accepted?.status === "READY" && (
                      <Button ref={saveRef} onClick={() => openSave()}>
                        Save preset
                      </Button>
                    )}
                </div>
              )}
              <div
                className="oms-report-download__footer-actions"
                role="group"
                aria-label="Export actions"
              >
                <Button
                  variant={current.jobId && preparing ? "primary" : "normal"}
                  onClick={close}
                >
                  Close
                </Button>
                {exportActions}
              </div>
            </>
          )}
          {(editor.view === "save" || editor.view === "manage") && (
            <>
              <Button
                disabled={presets.change.isLoading || presets.read.isLoading}
                onClick={() => backToExport()}
              >
                {editor.view === "save" ? "Back to settings" : "Back to export"}
              </Button>
              {editor.view === "save" && (
                <Button
                  variant="primary"
                  loading={presets.change.isLoading}
                  loadingText="Saving preset"
                  onClick={savePreset}
                  disabled={
                    (editor.updating && !edited) ||
                    presets.read.isLoading ||
                    (editor.scope !== "columns" && !validFilters) ||
                    (editor.scope !== "filters" && !validColumns)
                  }
                >
                  {editor.updating ? "Update preset" : "Save preset"}
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      <div ref={contentRef}>
        {editor.view === "manage" && (
          <OMSPresetManager
            state={presets}
            onEdit={(id) => requestPreset(id, true)}
            onDeleted={(id) => {
              if (applied?.presetId === id) setApplied(undefined);
              setUndo(undefined);
              setAnnouncement(
                "Preset deleted. Applied settings are preserved."
              );
            }}
            onRenamed={(metadata) => {
              if (applied?.presetId === metadata.presetId)
                setApplied({ ...applied, ...metadata });
            }}
          />
        )}
        {editor.view !== "manage" && (
          <SpaceBetween size="m">
            {editor.view !== "save" && (
              <OMSExportSource
                report={source}
                availableReport={availableReport}
                accepted={active}
                compact
                onUseLatest={() => {
                  changed();
                  if (previousDraft) {
                    setPreviousDraft(undefined);
                    setEditor({ view: "export", name: "", scope: "columns" });
                  }
                  const adoptedCriteria =
                    active && accepted
                      ? accepted.filters.map((filter, id) => ({
                          ...filter,
                          id,
                        }))
                      : criteria.criteria;
                  if (active && accepted) {
                    setMode(accepted.mode);
                    setColumns({
                      strategy: accepted.outputColumnStrategy || "all",
                      names: accepted.outputColumns || [],
                      customOrder: true,
                    });
                    setApplied(undefined);
                  }
                  setAttempted(true);
                  criteria.replace(
                    adoptedCriteria.map((criterion) => {
                      const previousType =
                        source.columnTypes?.[criterion.column] || "string";
                      const nextType =
                        availableReport.columnTypes?.[criterion.column] ||
                        "string";
                      return availableReport.columns.includes(
                        criterion.column
                      ) && previousType !== nextType
                        ? {
                            ...criterion,
                            valueNotice: `This field changed from ${previousType} to ${nextType}. Review its condition and value.`,
                          }
                        : criterion;
                    }),
                    active && accepted ? accepted.operation : criteria.operation
                  );
                  full.editSettings(availableReport);
                  filtered.editSettings(availableReport);
                  if (mode === "filtered") {
                    setWorkspace("rows");
                    setFocusFilters(true);
                  }
                  const nextColumns: OmsColumnsDraft =
                    active && accepted
                      ? {
                          strategy: accepted.outputColumnStrategy || "all",
                          names: accepted.outputColumns || [],
                          customOrder: true,
                        }
                      : columns;
                  if (nextColumns.strategy === "selected") {
                    setColumns({ ...nextColumns, touched: true });
                    if (columnSelectionError(nextColumns, availableReport)) {
                      setWorkspace("columns");
                      setFocusColumns(true);
                    }
                  }
                }}
              />
            )}
            {editor.view === "save" && (
              <Container
                header={
                  <Header variant="h3">
                    {editor.updating ? "Review preset changes" : "Save preset"}
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {(saveValidation ||
                    presets.change.isError ||
                    presets.read.isError) && (
                    <Alert
                      type={
                        expiredRequest && !presets.read.isError
                          ? "warning"
                          : "error"
                      }
                      header={saveErrorHeader}
                      action={saveRecoveryAction}
                    >
                      {saveValidation ||
                        presetErrorMessage(
                          presets.read.isError
                            ? presets.read.error
                            : presets.change.error
                        )}
                    </Alert>
                  )}
                  <OMSPresetSave
                    name={editor.name}
                    scope={editor.scope}
                    onName={(name) => setEditor({ ...editor, name })}
                    onScope={(scope: OmsPresetScope) =>
                      setEditor({ ...editor, scope })
                    }
                    validFilters={validFilters}
                    validColumns={validColumns}
                    attempted={saveAttempted}
                    nameRef={nameRef}
                    disabled={
                      presets.change.isLoading || presets.read.isLoading
                    }
                    reviewing={editor.updating}
                    currentPresetName={applied?.name}
                    onDestination={changeSaveDestination}
                  />
                  {editor.updating && applied && (
                    <OMSPresetChanges
                      saved={applied}
                      draft={configuration}
                      name={editor.name}
                      scope={editor.scope}
                      report={source}
                    />
                  )}
                  {!editor.updating && (
                    <OMSPresetContents
                      draft={configuration}
                      scope={editor.scope}
                      report={source}
                    />
                  )}
                </SpaceBetween>
              </Container>
            )}
            {editor.view !== "save" &&
              (current.jobId || !current.create.isIdle) && (
                <OMSExportPanel state={current} mode={mode} />
              )}
            {editor.view !== "save" && !active && restoredNotice && (
              <Alert
                type="info"
                dismissible
                dismissAriaLabel="Dismiss restored draft notice"
                onDismiss={() => setRestoredNotice(false)}
              >
                The earlier request may have completed. Your settings are
                retained so you can retry safely.
              </Alert>
            )}
            {presetsEnabled && !active && editor.view !== "save" && (
              <OMSPresetToolbar
                state={presets}
                applied={applied}
                disabled={active}
                saveDisabled={active && accepted?.status !== "READY"}
                modified={edited}
                editing={editingPreset}
                onRestore={
                  undo && !editingPreset ? () => undoSettings() : undefined
                }
                onCustom={() => {
                  changed();
                  setApplied(undefined);
                  setEditor({ view: "export", name: "", scope: "columns" });
                  selectRef.current?.focus();
                }}
                onApply={requestPreset}
                onSave={() =>
                  openSave(
                    Boolean(applied && (edited || editingPreset)),
                    editingPreset ? "edit" : "actions"
                  )
                }
                onManage={() => {
                  presets.change.reset();
                  setEditor({
                    ...editor,
                    view: "manage",
                    returnTo: editingPreset ? "edit" : "actions",
                  });
                }}
                saveRef={saveRef}
                selectRef={selectRef}
                manageRef={manageRef}
              />
            )}
            {editor.view !== "save" &&
              announcement &&
              !announcement.startsWith("Applied ") &&
              !announcement.startsWith("Editing ") && (
                <Alert
                  type="info"
                  dismissible
                  dismissAriaLabel="Dismiss preset notice"
                  onDismiss={() => setAnnouncement("")}
                >
                  {announcement}
                </Alert>
              )}
            {!active && editor.view !== "save" && (
              <>
                {source.rowCount === 0 && (
                  <Alert type="info" header="Empty report">
                    The export will contain headers only.
                  </Alert>
                )}
                {attempted && (issues.length > 0 || columnError) && (
                  <Alert type="warning" header="Complete your settings">
                    <ul className="oms-report-download__applied-filters">
                      {issues.map((issue) => (
                        <li key={`${issue.index}:${issue.control}`}>
                          <Button
                            onClick={() => {
                              setWorkspace("rows");
                              setFocusIssue(issue);
                              setFocusFilters(true);
                            }}
                          >
                            Filter {issue.index + 1}: {issue.message}
                          </Button>
                        </li>
                      ))}
                      {columnError && (
                        <li>
                          <Button
                            onClick={() => {
                              setWorkspace("columns");
                              setFocusColumns(true);
                            }}
                          >
                            {columnError}
                          </Button>
                        </li>
                      )}
                    </ul>
                  </Alert>
                )}
                {presets.read.isLoading && (
                  <Box color="text-body-secondary">
                    Wait for the preset to finish loading before preparing an
                    export.
                  </Box>
                )}
                <div className="oms-export-layout">
                  <div className="oms-export-layout__editor">
                    <Tabs
                      ariaLabel="Report export settings"
                      activeTabId={columnsEnabled ? workspace : "rows"}
                      onChange={({ detail }) =>
                        setWorkspace(detail.activeTabId as "rows" | "columns")
                      }
                      tabs={[
                        {
                          id: "rows",
                          label: "Rows",
                          contentRenderStrategy: "lazy",
                          content: (
                            /* Keyboard users can scroll the bounded active workspace. */
                            /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
                            <div
                              ref={workspace === "rows" ? paneRef : undefined}
                              className="oms-export-workspace"
                              style={{ maxHeight: paneMaxHeight }}
                              role="region"
                              aria-label="Rows settings"
                              tabIndex={0}
                            >
                              <SpaceBetween size="m">
                                <FormField
                                  className="oms-export-scope"
                                  label="Rows to export"
                                  constraintText="Dashboard filters do not apply."
                                >
                                  <Tiles
                                    columns={2}
                                    value={mode}
                                    items={[
                                      { value: "full", label: "All rows" },
                                      {
                                        value: "filtered",
                                        label: "Filtered rows",
                                      },
                                    ]}
                                    onChange={({ detail }) => {
                                      changed();
                                      const nextMode = detail.value as
                                        | "full"
                                        | "filtered";
                                      const target =
                                        nextMode === "full" ? full : filtered;
                                      if (
                                        target.jobId ||
                                        target.sourceReport.snapshotId !==
                                          source.snapshotId
                                      )
                                        target.editSettings(source);
                                      setMode(nextMode);
                                    }}
                                  />
                                </FormField>
                                {mode === "filtered" && (
                                  <div className="oms-export-filter-settings">
                                    <SpaceBetween size="s">
                                      <Header variant="h3">
                                        Report filters
                                      </Header>
                                      <OMSRecordFilters
                                        report={source}
                                        state={editableCriteria}
                                        embedded
                                        attempted={attempted}
                                        focusIssue={focusIssue}
                                        focusRequested={focusFilters}
                                        onFocusHandled={() =>
                                          setFocusFilters(false)
                                        }
                                      />
                                    </SpaceBetween>
                                  </div>
                                )}
                              </SpaceBetween>
                            </div>
                            /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                          ),
                        },
                        ...(columnsEnabled
                          ? [
                              {
                                id: "columns",
                                label: "Columns",
                                contentRenderStrategy: "lazy" as const,
                                content: (
                                  /* Keyboard users can scroll the bounded active workspace. */
                                  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
                                  <div
                                    ref={
                                      workspace === "columns"
                                        ? paneRef
                                        : undefined
                                    }
                                    className="oms-export-workspace oms-export-workspace--columns"
                                    style={{
                                      maxHeight: paneMaxHeight,
                                    }}
                                    role="region"
                                    aria-label="Column settings"
                                    tabIndex={0}
                                  >
                                    <OMSColumns
                                      availableHeight={paneMaxHeight}
                                      report={source}
                                      settings={columns}
                                      onChange={(settings) => {
                                        changed();
                                        setColumns(settings);
                                      }}
                                      attempted={attempted}
                                      focusRequested={focusColumns}
                                      onFocusHandled={() =>
                                        setFocusColumns(false)
                                      }
                                    />
                                  </div>
                                  /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </div>
                </div>
                {!columnsEnabled && columns.strategy === "selected" && (
                  <Alert type="warning">
                    New column selections are temporarily unavailable. Your
                    selection is preserved.
                    {previousRequest && " You can retry your previous request."}
                    <Button
                      onClick={() => {
                        changed();
                        setColumns({ ...columns, strategy: "all" });
                      }}
                    >
                      Use all columns
                    </Button>
                  </Alert>
                )}
              </>
            )}
            {active && accepted && editor.view !== "save" && (
              <SpaceBetween size="m">
                {(accepted?.csvFiles || 1) > 1 && (
                  <Box color="text-body-secondary">
                    This existing ZIP contains {accepted?.csvFiles} CSV files.
                    Edit settings to prepare one CSV.
                  </Box>
                )}
                <OMSSettingsSummary
                  report={source}
                  mode={accepted.mode}
                  filters={accepted.filters}
                  operation={accepted.operation}
                  columns={accepted.outputColumns || source.columns}
                  allColumns={accepted.outputColumnStrategy !== "selected"}
                  textCaseSensitive={accepted.textCaseSensitive ?? true}
                  result
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        )}
      </div>
    </Modal>
  );
}
